import dynamic from 'next/dynamic'
import { useMemo } from 'react'
// Components
import Footer from '@/components/newFooter'
import Header from '@/components/newHeader/Header'
import HeadSeo from '@/components/HeadSeo'
import Faqs from '@/components/landings/Faqs'
// Styles
import styles from './index.module.scss'
// Utils
import { getRedirectPath } from '@/components/landings/util/helper'
import { stateMap } from '@/utils/states'
import { getPost } from '@/components/blog/lib/cosmic'
import { DogsLandingsContext } from '@context/dogsLandingsContext'
// GQL Queries
import {
  STATE_LIST_BY_BREED,
  BREED_LIST_BY_STATE,
  GET_LANDINGS_BREED_DETAILS,
  BREED_LIST_EXPLORER,
  GET_NEARBY_DOGS,
  GET_STATE_FACTS_STATE_LANDING,
  PUPPY_SEARCH,
  STATE_LANDING,
  GET_RELATED_BREEDS,
} from '@/graphql/index'

const BreedLanding = dynamic(
  () => import('@/components/landings/BreedLanding'),
  { ssr: true },
)
const CategoryLanding = dynamic(
  () => import('@/components/landings/CategoryLanding'),
)
const StateLanding = dynamic(() => import('@/components/landings/StateLanding'))

export default function LandingPages({
  // States Landing
  breedsByState,
  breedsExplorer,
  allBreedPuppies,
  categoriesData,
  nearbyDogs,
  statesData,
  // Breeds Landing
  statesByBreed,
  relatedBreeds,
  // All Landings
  breedDetails,
  breedPuppies,
  faqData,
  landingType,
  variables,
  // reservedBreed
}) {
  const breed = breedDetails?.breed || {}
  const memoizedNationWideBreeds = useMemo(
    () => allBreedPuppies?.search?.rows,
    [],
  )
  const dogs = breedPuppies?.search?.rows
  const countDogs = breedPuppies?.search?.count

  return (
    <DogsLandingsContext.Provider value={{ variables, landingType }}>
      <HeadSeo breed={breed} variables={variables} landingType={landingType} />
      <div className={styles.main}>
        <Header />
        {landingType === 'breed' && (
          <BreedLanding
            dogs={dogs}
            countDogs={countDogs}
            breed={breed}
            landingType={landingType}
            statesByBreed={statesByBreed}
            relatedBreeds={relatedBreeds}
          />
        )}
        {landingType === 'category' && (
          <CategoryLanding
            dogs={dogs}
            countDogs={countDogs}
            variables={variables}
            landingType={landingType}
          />
        )}
        {landingType === 'state' && (
          <StateLanding
            dogs={dogs}
            countDogs={countDogs}
            categoriesData={categoriesData}
            breedsByState={breedsByState}
            breedsExplorer={breedsExplorer}
            nearbyDogs={nearbyDogs}
            statesData={statesData}
            landingType={landingType}
            variables={variables}
            breedsNationWide={memoizedNationWideBreeds}
          />
        )}
        <Faqs data={faqData} />
        <Footer />
      </div>
    </DogsLandingsContext.Provider>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  const apolloClient = await (
    await import('@/utils/apolloClient')
  ).initializeApollo()
  const categories = [
    'kid_friendly',
    'small',
    'hypoallergenic',
    'emotional_support',
    'guard',
    'active',
    'doodle',
    'service',
    'apartment',
    'affordable',
    'therapy',
    'trained',
  ]

  // Handle breed select by id or name slug
  let variables
  let landingType = ''
  const slug = params['breed-slug']

  if (params) {
    if (categories.includes(slug)) {
      // For categories landings
      variables = { category: slug }
      landingType = 'category'
    } else if (stateMap[slug]) {
      // For states landings
      // Searches for USA states are made by the state code in state keys. The Canada state search is done by the name in stateMap keys.
      if (stateMap[slug].state.length === 2) {
        // USA
        variables = {
          state: stateMap[slug].state,
          stateName: stateMap[slug].name,
          country: 'USA',
          stateSlug: slug,
          borderingStates: stateMap[slug].borderingState,
        }
      } else {
        // Canada
        variables = {
          state: slug,
          stateName: stateMap[slug].name,
          country: 'Canada',
          stateSlug: slug,
          borderingStates: stateMap[slug].borderingState,
        }
      }
      landingType = 'state'
    } else if (!isNaN(slug)) {
      variables = { id: +slug }
      landingType = 'breed'
    } else {
      // For breed landings
      variables = { slug }
      landingType = 'breed'
    }
  }

  // Handle redirects
  const redirectPath = await getRedirectPath(slug)

  if (redirectPath) {
    return {
      redirect: {
        destination: redirectPath,
        statusCode: 301,
      },
    }
  }

  // All landings
  let breedPuppies = {
    search: {
      rows: [],
    },
  }

  let allBreedPuppies = {}
  // Breed landings
  let breedDetails = {
    breed: {
      id: 0,
      name: '',
      slug: '',
      description: '',
      image: '',
      family: false,
      mini: false,
      hypoallergenic: false,
      companion: false,
      guard: false,
      hunting: false,
      active: false,
      doodles: false,
      service: false,
      apartment: false,
    },
  }
  let statesByBreed = {
    stateListByBreed: [],
  }
  // State landings
  let categoriesData = {}
  const breedsData = {
    breeds: [],
  }
  let nearbyDogs = {}
  let statesData = {}
  let explorer
  let breedsByState = {
    breedListByState: [],
  }
  let relatedBreeds = {}

  const faqData = await getPost({
    type: 'faqs',
    slug,
    content: ['slug', 'title', 'metadata'],
  })

  try {
    const { data } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: GET_LANDINGS_BREED_DETAILS,
      variables,
    })

    if (variables.id && landingType === 'breed') {
      return {
        redirect: {
          destination: `/puppies-for-sale/${data.breed.slug}`,
          statusCode: 301,
        },
      }
    }

    breedDetails = data

    const { data: _breedPuppies } = await apolloClient.query({
      fetchPolicy: 'no-cache',
      query: PUPPY_SEARCH,
      variables: {
        breedSlug: variables?.slug,
        category: variables?.category,
        state: variables?.state,
        sort: 'by-view-score',
        limit: 50,
      },
    })

    breedPuppies = _breedPuppies

    // eslint-disable-next-line no-console
    console.table(
      breedPuppies.search.rows.map((dog) => ({
        id: dog.id,
        name: dog.name,
        viewScore: dog.viewScore,
      })),
    )

    if (landingType === 'state') {
      const { data: _allBreedPuppies } = await apolloClient.query({
        fetchPolicy: 'no-cache',
        query: PUPPY_SEARCH,
        variables: {
          limit: 5,
          page: 1,
        },
      })

      allBreedPuppies = _allBreedPuppies

      const { data: _explorer } = await apolloClient.query({
        query: BREED_LIST_EXPLORER,
        variables: { limit: 12 },
      })

      explorer = _explorer

      const {
        data: { breeds = '', categories = '' },
      } = await apolloClient.query({ query: STATE_LANDING })

      // Breeds
      breedsData.breeds = breeds
      // Categories
      categoriesData = categories

      // States Facts

      const { data: _stateData = '' } = await apolloClient.query({
        query: GET_STATE_FACTS_STATE_LANDING,
        variables: { code: variables?.state },
      })

      statesData = _stateData

      const { data: breedsByStateList } = await apolloClient.query({
        query: BREED_LIST_BY_STATE,
        variables: { state: variables?.state },
      })

      breedsByState = breedsByStateList

      const { data: _nearby } = await apolloClient.query({
        query: GET_NEARBY_DOGS,
        variables: { borderingStates: variables?.borderingStates },
      })

      // grouped dogs by state
      const groupedDogs = {}
      _nearby?.nearbyStateDogs?.forEach((dog) => {
        if (!groupedDogs[dog.breeder.state]) {
          groupedDogs[dog.breeder.state] = []
        }
        groupedDogs[dog.breeder.state].push(dog)
      })

      nearbyDogs = groupedDogs
    }
  } catch (error) {
    console.error('ERROR LANDINGS', error)
    throw error
  }

  if (landingType === 'breed') {
    try {
      const { data: _statesByBreed } = await apolloClient.query({
        query: STATE_LIST_BY_BREED,
        variables: {
          slug: variables?.slug,
        },
      })
      statesByBreed = _statesByBreed

      const { data: _relatedBreeds } = await apolloClient.query({
        query: GET_RELATED_BREEDS,
        variables: {
          breedId: breedDetails?.breed?.id,
          slice: 65,
          limit: 65,
        },
      })

      relatedBreeds = _relatedBreeds
    } catch (error) {
      console.error('error BREED LIST landing', error)
      throw error
    }
  }

  if (breedDetails?.breed === null) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      // States Landing
      breedsByState: breedsByState?.breedListByState || {},
      breedsExplorer: explorer?.breedsList || {},
      categoriesData: categoriesData || {},
      nearbyDogs: nearbyDogs || {},
      allBreedPuppies: allBreedPuppies || {},
      statesData: statesData || {},
      // Breeds Landing
      statesByBreed: statesByBreed?.stateListByBreed || {},
      relatedBreeds: relatedBreeds || {},
      // All Landings
      breedDetails: breedDetails || {},
      breedPuppies: breedPuppies || {},
      faqData: faqData?.metadata?.faqs || [],
      landingType: landingType || '',
      variables: variables || {},
    },
    revalidate: 60 * 60 * 6, // Revalidate every 6 hours
  }
}
